/**
 * useVaultAutoSave - Hook para auto-guardado de formularios del vault
 *
 * Características:
 * - Debounce de 500ms para evitar escrituras excesivas
 * - Persistencia en localStorage vinculada a folderId + requirementId
 * - TTL de 7 días con limpieza automática
 * - Flush al cambiar de requirement, cerrar pestaña o ocultar la página
 * - Soporte para baseline del servidor y restauración segura
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  saveFormDraft,
  getFormDraft,
  clearFormDraft,
  hasFormDraft,
  type StoredFormDraft,
} from '../utils/vault/vaultFormStorage';
import { useAutoSaveContextOptional } from '../contexts/AutoSaveContext';

const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días

export interface AutoSaveState {
  /** Última vez que se guardó */
  lastSaved: Date | null;
  /** Si hay datos guardados previamente */
  hasStoredData: boolean;
  /** Si está en proceso de guardar */
  isSaving: boolean;
  /** Si hay cambios sin guardar */
  hasPendingChanges: boolean;
}

export interface UseVaultAutoSaveOptions<T = Record<string, unknown>> {
  /** Tiempo de debounce en ms (default: 500) */
  debounceMs?: number;
  /** Tiempo de vida del draft en ms (default: 7 días) */
  ttlMs?: number;
  /** Callback después de guardar */
  onSave?: () => void;
  /** Callback cuando se detectan datos guardados al montar */
  onDraftFound?: (draft: StoredFormDraft<T>) => void;
  /** Si el autosave está habilitado (default: true) */
  enabled?: boolean;
  /** Última actualización conocida del servidor para descartar drafts obsoletos */
  serverUpdatedAt?: string | Date | null;
}

interface RegisterSnapshotOptions {
  source?: 'server' | 'draft';
  lastSaved?: Date | null;
}

export interface UseVaultAutoSaveReturn<T> {
  /** Estado actual del autosave */
  autoSaveState: AutoSaveState;
  /** Guardar datos manualmente (con debounce) */
  saveData: (data: T) => void;
  /** Forzar guardado inmediato */
  forceSave: (data: T) => void;
  /** Limpiar el borrador guardado */
  clearDraft: () => void;
  /** Obtener datos guardados */
  getStoredData: () => T | null;
  /** Verificar si hay datos guardados */
  checkHasStoredData: () => boolean;
  /** Registrar el snapshot inicial o restaurado para evitar guardados fantasma */
  registerSnapshot: (data: T | null, options?: RegisterSnapshotOptions) => void;
  /** Forzar guardado del cambio pendiente actual */
  flushPendingSave: () => boolean;
}

const defaultAutoSaveState: AutoSaveState = {
  lastSaved: null,
  hasStoredData: false,
  isSaving: false,
  hasPendingChanges: false,
};

/**
 * Hook para auto-guardado de formularios del vault
 */
export function useVaultAutoSave<T = Record<string, unknown>>(
  folderId: string | undefined,
  requirementId: string | undefined,
  options: UseVaultAutoSaveOptions<T> = {}
): UseVaultAutoSaveReturn<T> {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    ttlMs = DEFAULT_TTL_MS,
    onSave,
    onDraftFound,
    enabled = true,
    serverUpdatedAt = null,
  } = options;

  const autoSaveContext = useAutoSaveContextOptional();

  const [autoSaveState, setAutoSaveState] = useState<AutoSaveState>(defaultAutoSaveState);

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedDataRef = useRef<string>('');
  const serverSnapshotRef = useRef<string>('');
  const pendingDataRef = useRef<T | null>(null);

  const hasValidIds = Boolean(folderId && requirementId);

  const resetState = useCallback(() => {
    setAutoSaveState(defaultAutoSaveState);
  }, []);

  const serializeData = useCallback((data: T | null): string => {
    if (data === null) return '';
    return JSON.stringify(data);
  }, []);

  const getServerTimestamp = useCallback((): number | null => {
    if (!serverUpdatedAt) return null;

    const timestamp =
      serverUpdatedAt instanceof Date
        ? serverUpdatedAt.getTime()
        : new Date(serverUpdatedAt).getTime();

    return Number.isNaN(timestamp) ? null : timestamp;
  }, [serverUpdatedAt]);

  useEffect(() => {
    if (autoSaveContext && enabled) {
      autoSaveContext.setAutoSaveState(autoSaveState);
    }
  }, [autoSaveContext, autoSaveState, enabled]);

  useEffect(() => {
    if (!enabled) {
      autoSaveContext?.resetAutoSaveState();
    }
  }, [autoSaveContext, enabled]);

  useEffect(() => {
    lastSavedDataRef.current = '';
    serverSnapshotRef.current = '';
    pendingDataRef.current = null;
    resetState();

    if (!hasValidIds || !enabled) return;

    const draft = getFormDraft<T>(folderId!, requirementId!);
    const serverTimestamp = getServerTimestamp();

    if (
      draft &&
      serverTimestamp !== null &&
      new Date(draft.savedAt).getTime() <= serverTimestamp
    ) {
      clearFormDraft(folderId!, requirementId!);
      return;
    }

    if (draft) {
      lastSavedDataRef.current = serializeData(draft.data);
      setAutoSaveState({
        lastSaved: new Date(draft.savedAt),
        hasStoredData: true,
        isSaving: false,
        hasPendingChanges: false,
      });
      onDraftFound?.(draft);
    }
  }, [
    enabled,
    folderId,
    getServerTimestamp,
    hasValidIds,
    onDraftFound,
    requirementId,
    resetState,
    serializeData,
  ]);

  const saveInternal = useCallback(
    (data: T): boolean => {
      if (!hasValidIds || !enabled) return false;

      const serialized = serializeData(data);

      if (serialized === lastSavedDataRef.current) {
        pendingDataRef.current = null;
        setAutoSaveState(prev => ({
          ...prev,
          isSaving: false,
          hasPendingChanges: false,
        }));
        return false;
      }

      if (serialized === serverSnapshotRef.current) {
        pendingDataRef.current = null;
        clearFormDraft(folderId!, requirementId!);
        lastSavedDataRef.current = serialized;
        resetState();
        return true;
      }

      const success = saveFormDraft<T>(folderId!, requirementId!, data, ttlMs);

      if (success) {
        pendingDataRef.current = null;
        lastSavedDataRef.current = serialized;
        setAutoSaveState({
          lastSaved: new Date(),
          hasStoredData: true,
          isSaving: false,
          hasPendingChanges: false,
        });
        onSave?.();
      } else {
        setAutoSaveState(prev => ({
          ...prev,
          isSaving: false,
        }));
      }

      return success;
    },
    [
      enabled,
      folderId,
      hasValidIds,
      onSave,
      requirementId,
      resetState,
      serializeData,
      ttlMs,
    ]
  );

  const flushPendingSave = useCallback((): boolean => {
    if (!hasValidIds || !enabled || pendingDataRef.current === null) {
      return false;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    return saveInternal(pendingDataRef.current);
  }, [enabled, hasValidIds, saveInternal]);

  useEffect(() => {
    return () => {
      flushPendingSave();
      autoSaveContext?.resetAutoSaveState();
    };
  }, [autoSaveContext, flushPendingSave]);

  useEffect(() => {
    if (!hasValidIds || !enabled || typeof window === 'undefined') return;

    const handlePageHide = () => {
      flushPendingSave();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushPendingSave();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, flushPendingSave, hasValidIds]);

  const saveData = useCallback(
    (data: T) => {
      if (!hasValidIds || !enabled) return;

      const serialized = serializeData(data);
      pendingDataRef.current = data;

      if (serialized === lastSavedDataRef.current) {
        pendingDataRef.current = null;
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        setAutoSaveState(prev => ({
          ...prev,
          isSaving: false,
          hasPendingChanges: false,
        }));
        return;
      }

      if (serialized === serverSnapshotRef.current) {
        pendingDataRef.current = null;
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        clearFormDraft(folderId!, requirementId!);
        lastSavedDataRef.current = serialized;
        resetState();
        return;
      }

      setAutoSaveState(prev => ({
        ...prev,
        hasPendingChanges: true,
      }));

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        setAutoSaveState(prev => ({ ...prev, isSaving: true }));
        saveInternal(data);
      }, debounceMs);
    },
    [
      debounceMs,
      enabled,
      folderId,
      hasValidIds,
      requirementId,
      resetState,
      saveInternal,
      serializeData,
    ]
  );

  const forceSave = useCallback(
    (data: T) => {
      if (!hasValidIds || !enabled) return;

      pendingDataRef.current = data;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }

      setAutoSaveState(prev => ({ ...prev, isSaving: true }));
      saveInternal(data);
    },
    [enabled, hasValidIds, saveInternal]
  );

  const clearDraft = useCallback(() => {
    if (!hasValidIds) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    clearFormDraft(folderId!, requirementId!);
    lastSavedDataRef.current = '';
    pendingDataRef.current = null;
    resetState();
  }, [folderId, hasValidIds, requirementId, resetState]);

  const getStoredData = useCallback((): T | null => {
    if (!hasValidIds) return null;
    const draft = getFormDraft<T>(folderId!, requirementId!);
    return draft?.data ?? null;
  }, [folderId, hasValidIds, requirementId]);

  const checkHasStoredData = useCallback((): boolean => {
    if (!hasValidIds) return false;
    return hasFormDraft(folderId!, requirementId!);
  }, [folderId, hasValidIds, requirementId]);

  const registerSnapshot = useCallback(
    (data: T | null, options: RegisterSnapshotOptions = {}) => {
      const { source = 'server', lastSaved = null } = options;
      const serialized = serializeData(data);

      pendingDataRef.current = null;
      lastSavedDataRef.current = serialized;

      if (source === 'server') {
        serverSnapshotRef.current = serialized;
      }

      setAutoSaveState(prev => ({
        ...prev,
        lastSaved: source === 'draft' ? lastSaved ?? prev.lastSaved : prev.lastSaved,
        hasStoredData: source === 'draft' ? true : prev.hasStoredData,
        isSaving: false,
        hasPendingChanges: false,
      }));
    },
    [serializeData]
  );

  return {
    autoSaveState,
    saveData,
    forceSave,
    clearDraft,
    getStoredData,
    checkHasStoredData,
    registerSnapshot,
    flushPendingSave,
  };
}

export default useVaultAutoSave;
